import { formatContactDisplayName, type BezoekersbestandEntry } from "@domaddress/shared";

function ToggleChip({ active, label }: { active: boolean; label: string }) {
  return (
    <span
      className="group-badge"
      style={{
        opacity: active ? 1 : 0.45,
        fontSize: "0.75rem",
      }}
      title={label}
    >
      {active ? "✓ " : "— "}
      {label}
    </span>
  );
}

function VolunteerStatusChip({ b }: { b: BezoekersbestandEntry }) {
  const wants = b.willing_volunteer;
  const linked = b.linked_contact_id != null;
  let bg = "rgba(0,0,0,0.06)";
  let color = "var(--text-400)";
  let title = "Geen aanvraag om vrijwilliger te worden";
  if (wants && linked) {
    bg = "rgba(22, 163, 74, 0.18)";
    color = "#15803d";
    title = "Wil vrijwilliger worden en staat al als vrijwilliger in de lijst";
  } else if (wants && !linked) {
    bg = "rgba(220, 38, 38, 0.14)";
    color = "#b91c1c";
    title = "Wil vrijwilliger worden — nog niet in de vrijwilligerslijst";
  } else {
    title = "Geen vrijwilligerswens aangegeven";
  }
  return (
    <span
      className="group-badge"
      style={{
        fontSize: "0.75rem",
        fontWeight: 600,
        background: bg,
        color,
        border: "1px solid rgba(0,0,0,0.06)",
      }}
      title={title}
    >
      Vrijwilliger
    </span>
  );
}

interface Props {
  b: BezoekersbestandEntry;
  canWrite: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onAddToSendList?: () => void;
}

/** Compact row for Bezoekersbestand list view. */
export default function BezoekerListRow({
  b,
  canWrite,
  onEdit,
  onDelete,
  onAddToSendList,
}: Props) {
  return (
    <div className="data-list-row bezoeker-list-row">
      <div className="data-list-row-main">
        <div className="contact-avatar contact-avatar--sm">
          <span>
            {(b.first_name[0] ?? "").toUpperCase()}
            {(b.last_name[0] ?? "").toUpperCase()}
          </span>
        </div>
        <div className="data-list-row-text">
          <div className="data-list-row-title">
            {formatContactDisplayName(b)}
            <span className="data-list-row-muted" style={{ marginLeft: 8, fontWeight: 400 }} title="Uniek kaartnummer in de database">
              #{b.id}
            </span>
          </div>
          <div className="data-list-row-meta">
            {b.birth_date && <span className="data-list-row-muted">Geb. {b.birth_date}</span>}
            {b.email && <span>{b.email}</span>}
            {b.phone && <span>{b.phone}</span>}
          </div>
          <div className="contact-groups" style={{ marginTop: "6px" }}>
            <ToggleChip active={b.wants_newsletter} label="Nieuwsbrief" />
            <ToggleChip active={b.wants_activities_info} label="Activiteiten" />
            <VolunteerStatusChip b={b} />
          </div>
          {b.linked_contact_id != null && (
            <span className="group-badge" style={{ background: "var(--primary)", color: "white", marginTop: "6px", display: "inline-block" }}>
              Gekoppeld aan contact #{b.linked_contact_id}
            </span>
          )}
        </div>
      </div>
      <div className="bezoeker-list-actions">
        {onAddToSendList && (
          <button type="button" className="btn-secondary btn-sm" onClick={onAddToSendList} title="Toevoegen aan zend-contacten">
            Toevoegen →
          </button>
        )}
        {canWrite && (
          <div className="data-list-row-actions">
            <button type="button" className="btn-icon" title="Bewerken" onClick={onEdit}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4Z" />
              </svg>
            </button>
            <button type="button" className="btn-icon btn-icon-danger" title="Verwijderen" onClick={onDelete}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="3,6 5,6 21,6" />
                <path d="M19,6l-1,14a2,2,0,0,1-2,2H8a2,2,0,0,1-2-2L5,6" />
                <path d="M10,11v6" />
                <path d="M14,11v6" />
                <path d="M9,6V4h6v2" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
